import React from 'react';
import tw from 'twin.macro';
import { css } from '@emotion/react';
import { RibbonCurvedLeft } from './scaffolds';
import { DateTime } from 'luxon';
import { ToastContainer, toast } from 'react-toastify';
import axios from 'axios';
import Cookie from 'js-cookie';
import Analytics from '../module/analytics';
import { delay } from 'bluebird';

const ProductContainer = tw.div`
  flex-wrap lg:flex lg:justify-start mb-4 text-left
`;

const FormBox: React.FC<{ style?: React.CSSProperties; className?: string; id?: string }> = ({
  children,
  style,
  className,
  id,
}) => (
  <div id={id} style={style} className={className}>
    <div tw="bg-white dark:bg-black-300 w-full h-full p-6 rounded-3xl flex flex-col justify-between">
      {children}
    </div>
  </div>
);

const ProductBox: React.FC<{ style?: React.CSSProperties; className?: string; id?: string }> = ({
  children,
  style,
  className,
  id,
}) => (
  <div id={id} style={style} className={className}>
    <div tw="bg-blue-lapis dark:bg-white  w-full h-full p-6 rounded-3xl flex flex-col justify-between">
      {children}
    </div>
  </div>
);

interface PackageProps {
  id?: string;
  title?: any;
  leftSubtitle?: any;
  leftContents?: PackageLeftContent[];
  products?: Product[];
  notes?: string;
  periodeWaktu?: Date;
  periodeWaktuEnd?: Date;
  periodeWaktuLabelOpened?: string;
  periodeWaktuLabelOnGoing?: string;
  periodeWaktuLabelClosed?: string;
  contactUs?: ContactUs;
}

interface ContactUs {
  form?: any;
  urlText?: string;
}

interface timeCountdown {
  days: string;
  hours: string;
  minutes: string;
  seconds: string;
}

interface PackageState {
  hoveringBenefit?: number;
  promoEnd?: string[];
  periodStart?: timeCountdown;
  periodEnd?: timeCountdown;
  periodeLabel?: string;
  width?: number;
  products?: Product[];
  formData?: any;
  loading: boolean;
  formSubmittedMessage?: string;
}

export interface PackageLeftContent {
  title?: string;
  detail?: any;
}

export interface SubtitleProduct {
  text: string;
  items?: any[];
}

export interface Product {
  title?: string;
  subtitle?: SubtitleProduct;
  estimation?: any;
  category?: string;
  ribbon?: any;
  ribbonStyle?: string;
  color?: string;
  darkColor?: string;
  promoEnd?: string;
  price?: string;
  specialPrice?: string;
  link?: string;
  startFrom?: boolean;
  tAC?: boolean;
  onLinkClicked?: any;
  linkText?: any;
  linkClass?: any;
  periode?: boolean;
}

class Package extends React.Component<PackageProps, PackageState> {
  timer!: NodeJS.Timer;
  test = 0;
  limit = 3;
  constructor(props: PackageProps) {
    super(props);
    this.state = {
      hoveringBenefit: null,
      promoEnd: this.props.products?.map(p => {
        if (!p.promoEnd || p.promoEnd === '') {
          return null;
        }
        return this.getCountdown(p.promoEnd);
      }),
      periodStart: this.checkFuture(this.props.periodeWaktu, true),
      periodEnd: this.checkFuture(this.props.periodeWaktuEnd, true),
      periodeLabel: this.setPeriodLabel(),
      width: typeof window !== 'undefined' ? window.innerWidth : 0,
      products: this.props.products,
      formData: {
        name: '',
        companyName: '',
        phoneNumber: '',
        emailEng: '',
        question: '',
      },
      loading: false,
      formSubmittedMessage: null,
    };
    this.onSubmitForm = this.onSubmitForm.bind(this);
    this.onChangeForm = this.onChangeForm.bind(this);
  }

  getGAClientID(): string {
    if (typeof window !== undefined) {
      return Cookie.get('_ga');
    }
    return null;
  }

  async onSubmitForm(event) {
    event.preventDefault();

    if (!this.state.formData.emailEng) {
      toast.error('Email cannot be empty', {
        position: 'top-center',
        autoClose: 5000,
        hideProgressBar: true,
        closeOnClick: false,
        pauseOnHover: false,
        draggable: false,
        progress: undefined,
      });
      return;
    }

    this.setState({ loading: true });
    try {
      const url = `https://easybiz-id.netlify.app/.netlify/functions/report-pma-form`;
      const analyticsData = Analytics();
      const gaClientId = this.getGAClientID();
      const bodyFormData = {
        gaClientId: gaClientId,
        cohort: 'original',
        source: analyticsData.source,
        medium: analyticsData.medium,
        companyName: this.state.formData?.companyName,
        name: this.state.formData?.name,
        userPhoneNumber: this.state.formData?.phoneNumber,
        email: this.state.formData?.emailEng,
        message: this.state.formData?.question,
      };
      let result = await axios({
        method: 'post',
        url,
        data: bodyFormData,
        headers: { 'Content-Type': 'application/json' },
      });
      this.setState({
        formData: {
          name: '',
          companyName: '',
          phoneNumber: '',
          emailEng: '',
          question: '',
        },
      });
      this.sendFormSubmissionSuccessEvent();
      toast.success(result?.data?.message || 'Success Send Email', {
        position: 'bottom-center',
        autoClose: 5000,
        hideProgressBar: true,
        closeOnClick: false,
        pauseOnHover: false,
        draggable: false,
        progress: undefined,
      });
      this.setState({ formSubmittedMessage: 'success' });
    } catch (error) {
      toast.error(error?.message, {
        position: 'bottom-center',
        autoClose: 5000,
        hideProgressBar: true,
        closeOnClick: false,
        pauseOnHover: false,
        draggable: false,
        progress: undefined,
      });
      this.setState({ formSubmittedMessage: 'Error, please try again later' });
    }

    this.setState({ loading: false });
    await delay(5000);
    this.setState({ formSubmittedMessage: null });
  }

  /**
   * add success form submission
   */
  sendFormSubmissionSuccessEvent() {
    if (typeof window !== 'undefined') {
      window.dataLayer = window.dataLayer ?? [];
      window.dataLayer.push({
        event: 'formSubmissionSuccess',
        formId: 'pma-contact',
      });
    }
  }

  onChangeForm(event) {
    this.setState({
      formData: { ...this.state.formData, [event.target.name]: event.target.value },
    });
  }

  getSufixPrice(price: string) {
    if (!price) {
      return '';
    }
    let num = parseInt(price);
    let sufix = num / 1000 >= 1000 ? 'mio' : 'k';
    return sufix;
  }

  formatPrice(price: string) {
    if (!price) {
      return '';
    }
    let num = parseInt(price);
    num = num / 1000 >= 1000 ? num / 1000000 : num / 1000;

    let formattedPrice = num.toString();
    return formattedPrice.replace('.', ',');
  }

  convertToKebabCase(source: string) {
    if (!source) {
      return source;
    }
    let text = source;
    if (text[text.length - 2] === ' ' || text[text.length - 3] === ' ') {
      text = text.slice(0, -2);
    }
    if (text[text.length - 1] === ' ') {
      text = text.slice(0, -1);
    }

    return text.toLocaleLowerCase().replace(/ /g, '-');
  }

  componentDidMount() {
    this.tick();
    window.addEventListener('resize', () => {
      this.setState({
        width: window.innerWidth,
      });
    });
  }

  componentWillUnmount() {
    this.resetTick();
    window.addEventListener('resize', () => {
      this.setState({
        width: window.innerWidth,
      });
    });
  }

  tick() {
    this.resetTick();
    this.timer = setInterval(() => {
      this.setState({
        promoEnd: this.props.products?.map(p => {
          if (!p.promoEnd || p.promoEnd === '') {
            return null;
          }
          const promo = DateTime.fromFormat(p.promoEnd, 'dd/MM/yyyy').toJSDate();
          return this.getCountdown(promo);
        }),
        periodStart: this.checkFuture(this.props.periodeWaktu, true),
        periodEnd: this.checkFuture(this.props.periodeWaktuEnd, true),
        periodeLabel: this.setPeriodLabel(),
      });
    }, 1000);
  }

  resetTick() {
    if (this.timer) {
      clearInterval(this.timer);
    }
  }

  getCountdown(date) {
    if (!date) {
      return null;
    }
    const dDay = DateTime.fromJSDate(date);
    const now = DateTime.local();
    const cntd = dDay.diff(now, ['days', 'hours', 'minutes', 'seconds']);
    if (cntd.days < 0 || cntd.hours < 0 || cntd.minutes < 0 || cntd.seconds < 0) {
      return '0:0:0';
    }
    const hour = `${cntd.hours < 10 ? '0' + cntd.hours : cntd.hours}:${
      cntd.minutes < 10 ? '0' + cntd.minutes : cntd.minutes
    }:${
      parseInt(cntd.seconds.toString(), 10) < 10
        ? '0' + parseInt(cntd.seconds.toString())
        : parseInt(cntd.seconds.toString())
    }`;

    if (cntd.days < 1) {
      return hour;
    }

    return `${cntd.days} Hari ${hour}`;
  }

  disableProductButton(): boolean {
    const timeStart = this.props.periodeWaktu;
    const timeEnd = this.props.periodeWaktuEnd;
    if (!timeStart || !timeEnd) {
      return false;
    }

    const start = DateTime.fromJSDate(new Date(timeStart));
    const now = DateTime.local();
    const end = DateTime.fromJSDate(new Date(timeEnd));

    if (now.toJSDate() < start.toJSDate()) {
      return true;
    }

    const cntd = end.diff(now, ['minutes']);
    if (cntd.minutes > 0 && cntd.minutes < 24 * 60) {
      return false;
    }

    if (now.toJSDate() >= start.toJSDate() && now.toJSDate() <= end.toJSDate()) {
      return false;
    }

    return true;
  }

  setPeriodLabel(): string {
    const timeStart = this.props.periodeWaktu;
    const timeEnd = this.props.periodeWaktuEnd;

    if (!timeStart || !timeEnd) {
      return '';
    }

    const start = DateTime.fromJSDate(new Date(timeStart));
    const now = DateTime.local();
    const end = DateTime.fromJSDate(new Date(timeEnd));

    if (now.toJSDate() < start.toJSDate()) {
      return this.props.periodeWaktuLabelOpened;
    }

    const cntd = end.diff(now, ['minutes']);
    if (cntd.minutes > 0 && cntd.minutes < 24 * 60) {
      return this.props.periodeWaktuLabelClosed;
    }

    if (now.toJSDate() >= start.toJSDate() && now.toJSDate() <= end.toJSDate()) {
      return this.props.periodeWaktuLabelOnGoing;
    }

    return '';
  }

  checkFuture(time = null, format = false): timeCountdown {
    if (!time) {
      return null;
    }

    const dDay = DateTime.fromJSDate(new Date(time));
    const now = DateTime.local();
    const cntd = dDay.diff(now, ['days', 'hours', 'minutes', 'seconds']);

    const days = cntd.days > 0 ? `${cntd.days < 10 ? '0' + cntd.days : cntd.days}` : '00';
    const hours = cntd.hours > 0 ? `${cntd.hours < 10 ? '0' + cntd.hours : cntd.hours}` : '00';
    const minutes =
      cntd.minutes > 0 ? `${cntd.minutes < 10 ? '0' + cntd.minutes : cntd.minutes}` : '00';
    const seconds =
      cntd.seconds > 0
        ? parseInt(cntd.seconds.toString(), 10) < 10
          ? '0' + parseInt(cntd.seconds.toString())
          : parseInt(cntd.seconds.toString())
        : '00';

    if (format) {
      return {
        days,
        hours,
        minutes,
        seconds: seconds.toString(),
      };
    }

    return {
      days: '00',
      hours: '00',
      minutes: '00',
      seconds: '00',
    };
  }

  render() {
    return (
      <section tw="bg-blue-marine dark:bg-blue-black relative" id={this.props.id}>
        <div>
          <div tw="container relative z-10">
            <div tw="lg:text-center mx-8 lg:mx-32 lg:text-center py-12">
              <h2 id="product" tw="text-white dark:text-white mb-8">
                {this.props.title}
              </h2>

              <div id="product-item-container">
                <div tw="mr-4 text-left lg:text-center">
                  <div tw="text-b1 text-black-100 dark:text-white mb-8">
                    {this.props.leftSubtitle}
                  </div>
                </div>
                <div tw="flex flex-col justify-between">
                  <div>
                    <ToastContainer />
                    <ProductContainer>
                      {this.state.products.map((item, i) => (
                        <ProductBox
                          tw="w-full lg:w-1/2 pb-4 lg:p-4 relative mb-6 lg:mb-4"
                          key={'products' + i}
                          id={this.convertToKebabCase(item.title)}
                        >
                          <div tw="text-white dark:text-black-100">
                            {item.ribbon ? (
                              <RibbonCurvedLeft
                                css={css`
                                  ${tw`-left-2 lg:left-0`}
                                  ${tw`top-2 lg:top-6`}
                                  ${tw`absolute`}
                                  .ribbonSpan {
                                    &:after {
                                      ${tw`bg-blue-lapis dark:bg-white`}
                                    }
                                  }
                                  .ribbon {
                                    ${item.ribbonStyle === 'bg-red'
                                      ? tw`bg-red`
                                      : tw`bg-blue-lapis dark:bg-white dark:text-blue-lapis`}
                                  }
                                  .ribbon:after {
                                    ${item.ribbonStyle === 'bg-red'
                                      ? tw`bg-red-dark`
                                      : tw`bg-blue-midnight dark:bg-black-500 dark:text-blue-lapis`}
                                  }
                                `}
                              >
                                {item.ribbon} {this.state?.promoEnd[i]}
                              </RibbonCurvedLeft>
                            ) : (
                              <div></div>
                            )}
                            <div tw="m-0 mt-8">
                              <p tw="mb-0 mt-2 text-h4 font-bold text-white dark:text-blue-black">
                                {item.startFrom ? 'Price starts from' : ''}
                              </p>
                              {!item.specialPrice ? (
                                <div tw="flex items-baseline text-white dark:text-blue-black">
                                  {item.price ? (
                                    <h2 tw="text-h1 m-0">
                                      IDR {this.formatPrice(item.price)}{' '}
                                      <b tw="text-h1">
                                        {this.getSufixPrice(item.price)}
                                        {item.tAC ? '*' : ''}
                                      </b>
                                    </h2>
                                  ) : (
                                    <h2 tw="m-0">Ask Us Via Whatsapp</h2>
                                  )}
                                </div>
                              ) : (
                                <div tw="flex items-baseline text-white dark:text-blue-black flex-wrap">
                                  <div
                                    tw="text-black-300 dark:text-black-700 flex items-baseline relative opacity-70"
                                    css={css`
                                      &:after {
                                        border-bottom: 3px solid;
                                        content: '';
                                        left: 0;
                                        position: absolute;
                                        right: 0;
                                        top: 33%;
                                        width: 98%;
                                        ${tw`border-black-300 dark:border-black-700`}
                                      }
                                    `}
                                  >
                                    <h2 tw="text-h1 m-0">
                                      IDR {this.formatPrice(item.price)}{' '}
                                      <b tw="text-h1">
                                        {this.getSufixPrice(item.price)}
                                        {item.tAC ? '*' : ''}
                                      </b>
                                    </h2>
                                  </div>
                                  <div tw="flex items-baseline">
                                    <h2 tw="text-h1 m-0">
                                      IDR {this.formatPrice(item.specialPrice)}{' '}
                                      <b tw="text-h1">
                                        {this.getSufixPrice(item.price)}
                                        {item.tAC ? '*' : ''}
                                      </b>
                                    </h2>
                                  </div>
                                </div>
                              )}
                            </div>
                            <p tw="text-h4 mb-2">{item.title}</p>
                            <div tw="text-h4 mb-2">
                              {item?.subtitle?.items?.length > 0 ? (
                                <ul
                                  tw="list-none m-0 my-3"
                                  css={css`
                                    li {
                                      ${tw`m-0 mb-2 flex items-center`}
                                    }
                                    ul {
                                      ${tw`mt-0`}
                                    }
                                  `}
                                >
                                  {item?.subtitle?.items?.map((it, i) => {
                                    return (
                                      <li key={i + '-it'}>
                                        <div
                                          css={css`
                                            ${tw`mr-4`}
                                            width: 32px;
                                          `}
                                        >
                                          <div
                                            tw="text-black-100"
                                            css={css`
                                              ${tw`bg-orange rounded-full text-center flex items-center justify-center`}
                                              height: 32px;
                                              width: inherit;
                                            `}
                                          >
                                            <span>{it.list}</span>
                                          </div>{' '}
                                        </div>
                                        <span>{it.text}</span>
                                      </li>
                                    );
                                  })}
                                </ul>
                              ) : (
                                <div
                                  dangerouslySetInnerHTML={{ __html: item?.subtitle?.text }}
                                ></div>
                              )}
                            </div>
                            <p tw="text-left text-b3 text-white dark:text-black-200 mb-4">
                              {this.props.notes}
                            </p>
                          </div>
                        </ProductBox>
                      ))}
                      <FormBox
                        tw="w-full lg:w-1/2 pb-4 lg:p-4 relative mb-6 lg:mb-4"
                        key={'form'}
                        id={'form'}
                      >
                        <form onSubmit={this.onSubmitForm} tw="grid grid-cols-6 gap-2">
                          {this.props?.contactUs?.form?.map((f, i) => {
                            return (
                              <div
                                key={`${f.contentfulid}-${i}-form`}
                                css={css`
                                  ${f.column === 1
                                    ? tw`col-span-6`
                                    : f.column === 2
                                    ? tw`col-span-3`
                                    : f.column === 3
                                    ? tw`col-span-2`
                                    : tw`col-span-1`}
                                  @media (max-width: 1024px) {
                                    ${tw`col-span-6`}
                                  }
                                `}
                              >
                                <label tw="w-full text-b2 my-2 lg:text-black-100 dark:text-white lg:dark:text-white">
                                  {f.label} <span tw="text-red">{f.required ? '*' : ''}</span>
                                </label>
                                <div
                                  tw="w-full my-2"
                                  css={css`
                                    textarea {
                                      ${tw`w-full rounded-2xl border-black-500 text-b1 p-4`}
                                      min-height: 48px;
                                      border: 1px solid #dadada;
                                      &:focus {
                                        ${tw`rounded-2xl`}
                                      }
                                      &:focus-visible {
                                        ${tw`outline-none`}
                                      }
                                    }

                                    input {
                                      ${tw`w-full rounded-2xl border-black-500 inline-block text-b1 px-4 box-border`}
                                      min-height: 48px;
                                      border: 1px solid #dadada;
                                      &:focus {
                                        ${tw`rounded-2xl`}
                                      }
                                      &:focus-visible {
                                        ${tw`outline-none`}
                                      }
                                    }
                                  `}
                                >
                                  {f.type === 'Textarea' ? (
                                    <textarea
                                      name={f.contentfulid}
                                      id={f.contentfulid}
                                      cols={30}
                                      rows={8}
                                      placeholder={f.placeholder}
                                      value={this.state.formData[f.contentfulid]}
                                      onChange={this.onChangeForm}
                                      required={f.required}
                                    ></textarea>
                                  ) : (
                                    <input
                                      name={f.contentfulid}
                                      id={f.contentfulid}
                                      type={f.type.toLowerCase()}
                                      placeholder={f.placeholder}
                                      value={this.state.formData[f.contentfulid]}
                                      onChange={this.onChangeForm}
                                      required={f.required}
                                    />
                                  )}
                                </div>
                              </div>
                            );
                          })}
                          {this.state.formSubmittedMessage ? (
                            <div tw="text-left text-b2 text-black-100 dark:text-white flex col-span-6">
                              <div tw="mr-2">
                                <span
                                  tw="rounded-full text-white flex items-center justify-center"
                                  css={css`
                                    font-size: 12px;
                                    width: 20px;
                                    height: 20px;
                                    ${
                                      this.state.formSubmittedMessage === 'success'
                                        ? 'background-color: #39b54a;'
                                        : 'background-color: #FF0000;'
                                    }";
                                  `}
                                >
                                  {this.state.formSubmittedMessage === 'success' ? '✔' : 'X'}
                                </span>
                              </div>
                              {this.state.formSubmittedMessage === 'success'
                                ? 'Thank You! We have successfully received your request. The Easybiz team will contact you within 1×24 hours via WhatsApp or phone (working day).'
                                : this.state.formSubmittedMessage}
                            </div>
                          ) : (
                            <input
                              disabled={this.state.loading}
                              type="submit"
                              value={this.props?.contactUs?.urlText || 'Send'}
                              tw="col-span-6 text-center bg-yellow-bright hover:bg-khaki active:bg-golden font-gilroy border-0 text-blue-marine font-semibold py-3 px-16 rounded-full cursor-pointer no-underline inline-block w-full"
                              css={css`
                                ${this.state.loading
                                  ? tw`bg-black-500 hover:bg-black-300 active:bg-black-500 cursor-not-allowed`
                                  : tw`bg-yellow-bright`}
                              `}
                            ></input>
                          )}
                        </form>
                      </FormBox>
                    </ProductContainer>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }
}

export default Package;
